import { useEffect, useId, useRef, useState } from 'react'
import { galleryImages } from '../../data/gallery'
import { callSupabaseFunction } from '../../lib/api'
import { mergePublicGallery, type PublicGalleryItem } from '../../lib/meta-gallery'
import { getGalleryJsonLd } from '../../lib/seo'
import { PageShell } from '../layout/page-shell'
import { PageTitle } from '../seo/page-title'
import { ButtonLink } from '../ui/button'

const staticItems: PublicGalleryItem[] = galleryImages.map((item, index) => ({
  id: `manual-${index}`,
  src: item.src,
  alt: item.alt,
  caption: item.caption,
  mediaType: 'IMAGE',
  source: 'manual',
}))

export interface GalleryPageProps {}

export function GalleryPage(_: Readonly<GalleryPageProps>) {
  const jsonLd = getGalleryJsonLd()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeId = useId()
  const [items, setItems] = useState<PublicGalleryItem[]>(staticItems)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const active = activeIndex == null ? null : items[activeIndex]

  useEffect(() => {
    let cancelled = false
    void callSupabaseFunction('gallery-feed').then(async (response) => {
      const payload = await response.json() as { ok?: boolean; items?: PublicGalleryItem[] }
      if (cancelled || !payload.ok) return
      setItems(mergePublicGallery({
        staticItems,
        socialItems: (payload.items ?? []).map((item) => ({
          source: 'instagram',
          mediaId: item.id,
          mediaType: item.mediaType,
          caption: item.caption ?? item.alt,
          permalink: item.permalink ?? null,
          sourceMediaUrl: item.src,
          sourceThumbnailUrl: item.src,
          cachedUrl: item.src,
          publishedAt: null,
          visibility: 'published',
          hashtagMatched: true,
          sourceStatus: 'active',
        })),
      }))
    }).catch(() => {
      if (!cancelled) setItems(staticItems)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (activeIndex == null) {
      if (dialog.open) dialog.close()
      return
    }
    if (!dialog.open) dialog.showModal()
  }, [activeIndex])

  useEffect(() => {
    if (activeIndex == null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        setActiveIndex((current) => (current == null ? current : (current + 1) % items.length))
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setActiveIndex((current) => (
          current == null ? current : (current - 1 + items.length) % items.length
        ))
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeIndex, items.length])

  return (
    <PageShell className="gallery-page">
      <PageTitle title="Our Work | Autoglow Detailing Geelong" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <section className="gallery-page-hero">
        <div className="section-container">
          <p className="eyebrow">Our Work</p>
          <h1>Studio gallery</h1>
          <p>
            Ceramic coating, paint correction and detailing from the Autoglow studio in Drysdale.
          </p>
        </div>
      </section>
      <section aria-labelledby="gallery-grid-heading" className="gallery-page-section">
        <div className="section-container">
          <h2 className="sr-only" id="gallery-grid-heading">Autoglow studio work</h2>
          <ul className="gallery-page-grid">
            {items.map((item, index) => (
              <li key={item.id}>
                <figure>
                  <button
                    aria-haspopup="dialog"
                    aria-label={`View larger: ${item.alt}`}
                    onClick={() => setActiveIndex(index)}
                    type="button"
                  >
                    <img alt={item.alt} height="720" loading="lazy" src={item.src} width="1080" />
                  </button>
                </figure>
              </li>
            ))}
          </ul>
          <div className="gallery-action">
            <ButtonLink href="/ceramic-protection" variant="ghost">Explore Ceramic Protection</ButtonLink>
            <ButtonLink href="/book">Book Now</ButtonLink>
          </div>
        </div>
      </section>
      <dialog
        aria-labelledby={closeId}
        className="gallery-lightbox"
        onClose={() => setActiveIndex(null)}
        ref={dialogRef}
      >
        {active ? (
          <div className="gallery-lightbox-inner">
            <p className="sr-only" id={closeId}>{active.alt}</p>
            <img alt={active.alt} height="1080" src={active.src} width="1620" />
            <div className="gallery-lightbox-actions">
              <button
                onClick={() => setActiveIndex((current) => (
                  current == null ? current : (current - 1 + items.length) % items.length
                ))}
                type="button"
              >
                Previous
              </button>
              <button onClick={() => setActiveIndex(null)} type="button">
                Close
              </button>
              <button
                onClick={() => setActiveIndex((current) => (
                  current == null ? current : (current + 1) % items.length
                ))}
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </dialog>
    </PageShell>
  )
}

export default GalleryPage
